import * as React from 'react'
import { createPortal } from 'react-dom'
import { UseFormGetValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ModalWrapper } from '~/atoms/modal/ModalWrapper'
import { SearchFilter } from '~/organisms/search/SearchFilter'
import {
  SearchCondition,
  makeSearchConditionSummaryText,
} from '~/organisms/search/SearchCondition'
import { SearchDate } from '~/organisms/search/SearchDate'
import { SearchTime } from '~/organisms/search/SearchTime'

// images
import SearchIcon from './search.svg?reactCompornt'
import CalendarIcon from './calendar.svg?reactCompornt'
import TimeIcon from './icon_time.svg?reactCompornt'
import SortIcon from './sort_icon.svg?reactCompornt'

import * as Style from './style.css'
import * as classNames from 'classnames'

export interface IProps {
  modalType: string
  prefectures: [string, string][]
  searchResultFormSummariesFlex: boolean
  sittersTotalCount: number
  sittersViewFromCount: number
  sittersViewToCount: number
  sortOrderList: [string, string][]
  isLogin: boolean
  searchState: ISearchState
  enableHousekeepingSubsidy?: boolean
  getValues: UseFormGetValues<ISearchState>
  setModal: (modalType: string) => void
  submitHandler: (search: ISearchState) => void
  searchOrderClickHandler: () => void
  searchOrderChangeHandler: (changed_order: ISearchOrderType) => void
}

export const SearchResultForm = ({
  modalType,
  prefectures,
  searchResultFormSummariesFlex,
  sittersTotalCount,
  sittersViewFromCount,
  sittersViewToCount,
  sortOrderList,
  isLogin,
  searchState,
  enableHousekeepingSubsidy,
  getValues,
  setModal,
  submitHandler,
  searchOrderClickHandler,
  searchOrderChangeHandler,
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const [isInitialized, setInitialized] = React.useState(false)
  React.useEffect(() => {
    setInitialized(true)
  }, [true])

  const searchConditionSubmitHandler = (state: ISearchConditionState) => {
    const search = { ...getValues('search'), ...state.search }
    const regulerSearch = {
      ...getValues('regular_search'),
      ...state.regular_search,
    }
    if (state.reserveType === 'regular') {
      search.work_date_from = undefined
    }
    submitHandler({
      search,
      regular_search: regulerSearch,
      reserveType: state.reserveType,
    })
  }

  const workDateFrom = getValues('search.work_date_from')
  const searchDateSubmitHandler = (state: ISearchDateState) => {
    const search = { ...getValues('search'), ...state.search }
    const regulerSearch = getValues('regular_search')
    const reserveType = getValues('reserveType')
    submitHandler({ search, regular_search: regulerSearch, reserveType })
  }
  const minDate = new Date()
  const maxDate = new Date(minDate.getFullYear() + 2, 11, 31)

  const timeFrom = getValues('search.work_time_from')
  const timeTo = getValues('search.work_time_to')
  const searchTimeSubmitHandler = (state: ISearchTimeState) => {
    const search = { ...getValues('search'), ...state.search }
    const regulerSearch = getValues('regular_search')
    const reserveType = getValues('reserveType')
    submitHandler({ search, regular_search: regulerSearch, reserveType })
  }

  const initialSearchOrder = () => {
    if (isLogin && searchState.search.order === undefined) {
      return 'live_nearby'
    } else if (!isLogin && searchState.search.order === undefined) {
      return 'recommend'
    } else {
      return searchState.search.order
    }
  }
  const searchOrder = initialSearchOrder()

  return (
    <div className={Style.SearchResultForm}>
      <div
        className={classNames({
          [Style.SearchResultFormSummaries]: true,
          [Style.SearchResultFormSummariesFlex]: searchResultFormSummariesFlex,
        })}
      >
        <div className={Style.SearchResultFormSummaryWrapper}>
          <div
            className={Style.SearchResultFormSummaryCondition}
            onClick={() => setModal('filter')}
          >
            <SearchIcon
              className={Style.SearchResultFormSummaryConditionIcon}
            />
            <span className={Style.SearchResultFormSummaryTextActive}>
              {makeSearchConditionSummaryText(
                searchState.search,
                searchState.regular_search,
                prefectures,
                searchState.search.geocode,
                searchState.search.station,
                searchState.reserveType,
                t,
              )}
            </span>
          </div>
          <div
            className={
              searchState.reserveType !== 'regular'
                ? Style.SearchResultFormSummaryDate
                : Style.SearchResultFormSummaryDateDisabled
            }
            onClick={() =>
              searchState.reserveType !== 'regular' && setModal('day')
            }
          >
            <CalendarIcon className={Style.SearchResultFormSummaryDateIcon} />
            <span
              className={
                workDateFrom
                  ? Style.SearchResultFormSummaryTextActive
                  : Style.SearchResultFormSummaryText
              }
            >
              {workDateFrom
                ? workDateFrom.getFullYear().toString() +
                  '年' +
                  (workDateFrom.getMonth() + 1).toString() +
                  '月' +
                  workDateFrom.getDate().toString() +
                  '日'
                : '日付'}
            </span>
          </div>
          <div
            className={Style.SearchResultFormSummaryTime}
            onClick={() => setModal('time')}
          >
            <TimeIcon className={Style.SearchResultFormSummaryTimeIcon} />
            <span
              className={
                timeFrom || timeTo
                  ? Style.SearchResultFormSummaryTextActive
                  : Style.SearchResultFormSummaryText
              }
            >
              {timeFrom || timeTo ? '' : '時間'}
              {timeFrom}
              {!timeFrom && timeTo ? '指定しない' : ''}
              {timeFrom || timeTo ? ' - ' : ''}
              {timeTo}
              {timeFrom && !timeTo ? '指定しない' : ''}
            </span>
          </div>
        </div>
        <SearchFilter getValues={getValues} />
      </div>
      <div>
        {isInitialized &&
          createPortal(
            <>
              <ModalWrapper
                mobileAnimation="slideUp"
                isOpen={modalType === 'filter'}
              >
                <div className={Style.SearchResultFormModalLarge}>
                  <SearchCondition
                    prefectures={prefectures}
                    enableHousekeepingSubsidy={enableHousekeepingSubsidy}
                    getValues={getValues}
                    submitHandler={searchConditionSubmitHandler}
                    closeHandler={() => setModal('')}
                    submitLabel="検索"
                    showSearchCount={true}
                  />
                </div>
              </ModalWrapper>
              <ModalWrapper
                mobileAnimation="fadeIn"
                isOpen={modalType === 'day'}
              >
                <div className={Style.SearchResultFormModalSmall}>
                  <SearchDate
                    getValues={getValues}
                    submitHandler={searchDateSubmitHandler}
                    closeHandler={() => setModal('')}
                    submitLabel="検索"
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </div>
              </ModalWrapper>
              <ModalWrapper
                mobileAnimation="fadeIn"
                isOpen={modalType === 'time'}
              >
                <div className={Style.SearchResultFormModalSmall}>
                  <SearchTime
                    submitLabel="検索"
                    getValues={getValues}
                    submitHandler={searchTimeSubmitHandler}
                    closeHandler={() => setModal('')}
                  />
                </div>
              </ModalWrapper>
            </>,
            document.body,
          )}
      </div>
      {sittersTotalCount > 0 && (
        <div className={Style.SearchOrder}>
          <div className={Style.SearchOrderCount}>
            <div className={Style.SearchOrderResultLabel}>検索結果：</div>
            <div className={Style.SearchOrderTotal}>
              {sittersTotalCount}件中
            </div>
            <div className={Style.SearchOrderViewCount}>
              {sittersViewFromCount}～{sittersViewToCount}件
            </div>
          </div>
          <div className={Style.SearchOrderDropdown}>
            <label
              htmlFor="search-order"
              className={Style.SearchOrderDropdownLabel}
            >
              <SortIcon className={Style.SearchOrderDropdownIcon} />
            </label>
            <select
              id="search-order"
              className={Style.SearchOrderDropdownSelect}
              value={searchOrder}
              onClick={searchOrderClickHandler}
              onChange={(event) =>
                searchOrderChangeHandler(event.target.value as ISearchOrderType)
              }
            >
              {sortOrderList.map((values) => (
                <option value={values[1]} key={values[1]}>
                  {values[0]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  )
}
